import { cn } from "@babackapp/customer-ui/lib/utils";
import { motion } from "motion/react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export type ReasonOption = {
  id: string;
  label: string;
  gapBefore?: number;
};

type ReasonOptionSelectorProps = {
  value: string;
  options: ReasonOption[];
  onChange: (value: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
};

const MIN_ITEM_H = 40;
const DEFAULT_GAP = 4;
const SPRING = { type: "spring", stiffness: 300, damping: 30 } as const;

/** Measures each option button, so a label wrapping on several lines is not clipped. */
function useItemHeights(count: number) {
  const nodes = useRef<(HTMLElement | null)[]>([]);
  const [heights, setHeights] = useState<number[]>([]);

  const measure = useCallback(() => {
    setHeights((previous) => {
      const next = nodes.current
        .slice(0, count)
        .map((node) => node?.offsetHeight ?? MIN_ITEM_H);
      const unchanged =
        previous.length === next.length &&
        previous.every((value, i) => value === next[i]);
      return unchanged ? previous : next;
    });
  }, [count]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    const observer = new ResizeObserver(measure);
    for (const node of nodes.current) if (node) observer.observe(node);
    return () => observer.disconnect();
  }, [measure]);

  const registerNode = useCallback((index: number) => (node: HTMLElement | null) => {
    nodes.current[index] = node;
  }, []);

  const heightAt = (index: number) => heights[index] ?? MIN_ITEM_H;

  return { registerNode, heightAt };
}

export function ReasonOptionSelector({ value, options, onChange, onOpenChange }: ReasonOptionSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { registerNode, heightAt } = useItemHeights(options.length);

  useEffect(() => {
    onOpenChange?.(isExpanded);
  }, [isExpanded, onOpenChange]);

  // y offset of each item when the list is expanded
  const expandedY = options.map((_, n) =>
    options
      .slice(0, n)
      .reduce(
        (acc, _, i) => acc + heightAt(i) + (options[i + 1]?.gapBefore ?? DEFAULT_GAP),
        0,
      ),
  );

  const selectedIndex = options.findIndex((option) => option.id === value);
  const expandedHeight =
    (expandedY.at(-1) ?? 0) + heightAt(options.length - 1) + DEFAULT_GAP;
  const collapsedHeight = heightAt(selectedIndex);

  const handleClick = (id: string) => {
    if (isExpanded) {
      onChange(id);
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  };

  return (
    <motion.div
      className="relative overflow-hidden"
      animate={{ height: isExpanded ? expandedHeight : collapsedHeight }}
      transition={SPRING}
    >
      {options.map((option, index) => {
        const isSelected = option.id === value;
        return (
          <motion.button
            key={option.id}
            ref={registerNode(index)}
            type="button"
            onClick={() => handleClick(option.id)}
            className={cn(
              "absolute inset-x-0 top-0 flex min-h-10 w-full cursor-pointer items-center rounded-lg border border-border/80 bg-background px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground",
              isSelected && !isExpanded ? "font-medium" : "font-normal",
              isSelected ? "z-10" : "z-0",
            )}
            animate={{
              y: isExpanded ? expandedY[index] : 0,
              opacity: isExpanded || isSelected ? 1 : 0,
            }}
            transition={SPRING}
          >
            {option.label}
          </motion.button>
        );
      })}
    </motion.div>
  );
}
